import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
// import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { InputStyle, TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhoneNumber } from "@sunSource/Common/Utilities/formatPhoneNumber";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    validateConfirmPasswordHandler,
    validatePasswordHandler,
} from "@sunSource/Common/Utilities/validate";
import {
    FormErrorsStateType,
    intialErrorFormState,
    newCustomerValidatorConfig,
    ValidatorConfigType,
} from "@sunSource/Components/CreateAccountNewFormValidatorConfig";
import checkUserExists from "@sunSource/Handlers/NewOnePageCheckout/OnePageCheckoutCheckExistingUser";
import setOnePageCreateAccountData from "@sunSource/Handlers/NewOnePageCheckout/OnePageCheckoutCreateAccountHandler";
import addAccount from "@sunSource/Store/Accounts/Handlers/AddAccount";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import SetOnePageTabData from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/OnePageCheckoutTabHandler";
import signIn from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/SignIn";
import {
    getOnePageCheckoutAccountState,
    getOnePageCheckoutTabState,
} from "@sunSource/Store/Data/NewOnePageCheckout/OnePageCheckoutStateSelectors";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MessageFilterStyles {
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioButton?: FieldSetPresentationProps<RadioComponentProps>;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        tabState: getOnePageCheckoutTabState(state),
        createAccountData: getOnePageCheckoutAccountState(state),
        userName: state.context.session?.userName,
        userLabel: state.context.session?.userLabel,
        isAuthenticated: state.context.session?.isAuthenticated,
        userFirstName: state.context.session?.firstName,
        userLastName: state.context.session?.lastName,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
    };
};

const mapDispatchToProps = {
    signIn,
    SetOnePageTabData,
    setOnePageCreateAccountData,
    checkUserExists,
    signOut,
};

export interface CreateAccountNewFormStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    fieldsGridItem?: GridItemProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    firstNameGridItem?: GridItemProps;
    jobTitleGridItem?: GridItemProps;
    companyNameGridItem?: GridItemProps;
    addressLineOneGridItem?: GridItemProps;
    addressLineTwoGridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    zipCodeGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    jobTitleTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    companyNameTextField?: TextFieldPresentationProps;
    cityTextField?: TextFieldPresentationProps;
    zipCodeTextField?: TextFieldPresentationProps;
    addressLineOneTextField?: TextFieldPresentationProps;
    addressLineTwoTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    stateGridItem?: GridItemProps;
    businessTypeGridItem?: GridItemProps;
    stateTextField?: SelectPresentationProps;
    countryGridItem?: GridItemProps;
    countryTextField?: SelectPresentationProps;
    phoneNumberGridItem?: GridItemProps;
    faxGridItem?: GridItemProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    faxTextField?: TextFieldPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    companyTypeText?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    fieldSetParentDiv?: InjectableCss;
    messageFilter?: MessageFilterStyles;
    requiredFieldDiv?: TypographyProps;
    shipToBillToCheck?: InjectableCss;
    passwordValidationMessage?: InjectableCss;
    loginBtn?: ButtonPresentationProps;
    lostYourPasswordSection?: GridItemProps;
    loginButtonSection?: GridItemProps;
    lostYourPassword?: ClickablePresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    logOut?: ClickablePresentationProps;
    userDetailsWrapper?: InjectableCss;
    userLabel?: InjectableCss;
    userName?: InjectableCss;
    changeLabel?: InjectableCss;
    companyDetailsGridItem?: GridItemProps;
    logOutGridItem?: GridItemProps;
}

export const createAccountNewFormStyles: CreateAccountNewFormStyles = {
    requiredFieldDiv: {
        css: css`
            > ${TypographyStyle} {
                margin-top: 15px;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            svg {
                width: 25px;
                height: 25px;
            }
        `,
    },
    mainGridContainer: {
        css: css`
            padding: 0px 15px;
            margin-top: 40px;
            margin-bottom: 20px;
            [class^="GridItemStyle"] {
                padding-bottom: 5px;
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            [class^="GridOffset"] {
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            .containerTitle {
                width: 100%;
                border-bottom: 2px solid ${getColor("primary")};
                background: ${getColor("info.contrast")};
                padding: 10px 0px;
                display: flex;
                margin: 10px 15px 0;
                color: ${getColor("primary")};
                > ${TypographyStyle} {
                    &:first-child {
                        font-weight: bold;
                    }
                }
                [class^="CheckboxGroupStyle"] {
                    margin-left: 30px;
                }
            }
            [class^="FormInputWrapper"] {
                margin-top: 0 !important;
                > ${InputStyle} {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
                > select[class^="SelectStyle"] {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
            }
        `,
    },
    businessTypeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            > div[class^="RadioGroupStyle"] {
                justify-content: flex-start;
                display: inline-block;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                > div[class^="RadioStyle"] {
                    margin-top: 0;
                    margin-right: 25px;
                    display: inline-block;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            null,
                            null,
                            null,
                        ])}
                    &:last-child {
                        margin-right: 0;
                    }
                    > label[class^="TypographyStyle"] {
                        font-size: 13px;
                    }
                }
            }
        `,
    },
    fieldsGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    countryGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            display: none;
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    stateGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding: 0px 0px 0px 15px;
        `,
    },
    submitErrorGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: end;
            padding: 0px 0px 35px 0px;
        `,
    },
    emailTextField: {
        cssOverrides: {
            inputSelect: css`
                font-size: 16px;
                padding: 0 2rem 0 10px !important;
                &:focus {
                    padding: 0 2rem 0 10px !important;
                }
            `,
        },
    },
    fieldSetParentDiv: {
        css: css`
            display: inline-block;
            vertical-align: top;
            > ${RadioStyle} {
                white-space: nowrap;
            }
        `,
    },
    shipToBillToCheck: {
        css: css`
            margin-top: 15px;
            margin-left: 15px;
            width: 100%;
        `,
    },
    companyTypeText: {
        size: 13,
        weight: "bold",
    },
    passwordValidationMessage: {
        css: css`
            ul {
                list-style: initial;
                padding-left: 20px;
                font-size: 14px;
            }
        `,
    },

    loginBtn: {
        variant: "tertiary",
        css: css`
            width: 120px;
            text-transform: uppercase;
            > ${TypographyStyle} {
                font-weight: 400;
            }
        `,
    },
    lostYourPasswordSection: {
        width: [12, 12, 6, 6, 6],
        css: css``,
    },
    lostYourPassword: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    logOut: {
        css: css`
            color: ${getColor("primary")};
            font-weight: 600;
        `,
    },
    loginButtonSection: {
        width: [12, 12, 6, 6, 6],
        css: css`
            justify-content: right;
        `,
    },
    userDetailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    userLabel: {
        css: css`
            font-weight: 600;
        `,
    },
    userName: {
        css: css`
            color: gray;
        `,
    },
    changeLabel: {
        css: css`
            color: #00539b;
            font-weight: 600;
            cursor: pointer;
        `,
    },
    companyDetailsGridItem: {
        width: 6,
        css: css`
            padding-top: 0px;
        `,
    },
    logOutGridItem: {
        width: 6,
        css: css`
            justify-content: right;
            padding-top: 0px;
        `,
    },
    resetPasswordModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                > ${TypographyStyle} {
                    color: ${getColor("secondary")};
                }
            `,
        },
    },
};

const styles = createAccountNewFormStyles;

const StyledForm = getStyledWrapper("form");

const OnePageLoginFormNew = ({
    accountSettings,
    signInUrl,
    createAccountData,
    signIn,
    tabState,
    SetOnePageTabData,
    setOnePageCreateAccountData,
    checkUserExists,
    userName,
    userLabel,
    isAuthenticated,
    userFirstName,
    userLastName,
    signOut,
    onePageCheckoutPageUrl
}: Props) => {
    const firstNameRequiredFieldMessage = siteMessage("First name is Required");
    const lastNameRequiredFieldMessage = siteMessage("Last name is Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");

    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);
    const [firstName, setFirstName] = useState(createAccountData?.firstName);
    const [lastName, setLastName] = useState(createAccountData?.lastName);
    const [email, setEmail] = useState(createAccountData?.email ?? "");
    const [isExistingUser, setIsExistingUser] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormReady, setIsFormReady] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [notYouClicked, setNotYouClicked] = useState(false);

    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormSubmitted, setFormSubmitted] = React.useState(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);

    const [password, setPassword] = React.useState("");
    const [confirmPassword, setConfirmPassword] = React.useState("");
    const [showPassword, setShowPassword] = React.useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState<React.ReactNode>("");
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState<React.ReactNode>("");

    const [firstNameError, setFirstNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : firstNameRequiredFieldMessage,
    );

    const [lastNameError, setLastNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : lastNameRequiredFieldMessage,
    );

    React.useEffect(() => {
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    }, [formErrors]);

    React.useEffect(() => {
        const cloneFormErrors = { ...formErrors };
        for (const [key] of Object.entries(cloneFormErrors)) {
            const element: HTMLSelectElement | HTMLInputElement | null = document.querySelector(`[name="${key}"]`);
            if (element && element.value) {
                delete cloneFormErrors[key as keyof FormErrorsStateType];
                setFormErrors(removeKey(cloneFormErrors, key));
            }
        }
    }, []);

    const resetForm = () => {
        setFirstName("");
        setFirstNameError("");
        setLastName("");
        setLastNameError("");
        setEmail("");
        setEmailError("");
        setPassword("");
        setConfirmPassword("");
        setPasswordErrorMessage("");
        setConfirmPasswordErrorMessage("");
    };

    const validateSubmitDisabled = (errorsState: FormErrorsStateType) => {
        return Object.keys(errorsState).length !== 0;
    };

    const removeKey = (formErrorObject: FormErrorsStateType, deleteKey: string) => {
        return Object.assign(
            {},
            ...Object.entries(formErrorObject)
                .filter(([k]) => k !== deleteKey)
                .map(([k, v]) => ({ [k]: v })),
        );
    };

    const validateHandler = (input: HTMLInputElement | HTMLSelectElement, customValidation?: CallbackType): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = newCustomerValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(emailError || firstNameError || lastNameError || passwordErrorMessage || confirmPasswordErrorMessage);
    };

    const firstNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setFirstName(input.value);
        setOnePageCreateAccountData({ firstName: input.value });
    };
    const lastNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setLastName(input.value);
        setOnePageCreateAccountData({ lastName: input.value });
    };

    const emailChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setEmail(input.value);
        setOnePageCreateAccountData({ email: input.value });
    };
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (email && !formErrors.email) {
                if (email) {
                    setIsLoading(true);
                    confirmHandler();
                }
            }
        }, 500); // Wait for 2 seconds after user stops typing

        return () => clearTimeout(delayDebounceFn); // Clear timeout if user types again
    }, [formErrors.email, email]);

    const isInContentAdmin = () => {
        try {
            const parentUrl = window?.parent?.location?.toString().toLowerCase();
            return parentUrl.includes("/contentadmin/");
        } catch (error) {
            // Catch cross-origin errors (if parent URL can't be accessed)
            return false;
        }
    };
    useEffect(() => {
        if (isInContentAdmin()) {
            completeAction(0);
        }
    }, []);
    useEffect(() => {
        if (
            firstName &&
            lastName &&
            email &&
            !formErrors.firstName &&
            !formErrors.lastName &&
            !formErrors.email &&
            isFormReady
        ) {
            setOnePageCreateAccountData({ firstName, lastName, email });
            completeAction(0);
        }
    }, [firstName, lastName, email, formErrors, isLoading]);

    const passwordChangeHandler = (input: HTMLInputElement) => {
        // validateConfirmPassword(input.value, confirmPassword);
        validatePassword({
            password: input.value,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        setPassword(input.value);
        pwdHandler(input);
    };

    const confirmPasswordChangeHandler = (input: HTMLInputElement) => {
        setConfirmPassword(input.value);
        validateConfirmPassword(password, input.value);
        pwdHandler(input);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsButtonDisabled(true);
        toasterContext.addToast({
            body: siteMessage("Your account has been successfully created"),
            messageType: "success",
            timeoutLength: 2500,
        });
        function getDataLayer() {
            return (window as any).dataLayer;
        }
        const dataLayer = getDataLayer();
        if (dataLayer) {
            dataLayer.push({
                event: "sign_up",
                sign_up_type: "new",
            });
        }
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            setIsButtonDisabled(false);
            return;
        }

        if (!isSubmitted) {
            setIsSubmitted(true);
        }
    };

    const pwdHandler = (input: HTMLInputElement) => {
        const pwd = input.name === "password" ? input.value : password;
        const confirmPwd = input.name === "confirmPassword" ? input.value : confirmPassword;

        const isValidate: ErrorObjectType | undefined =
            input.name === "password"
                ? validatePasswordHandler(pwd, confirmPwd)
                : validateConfirmPasswordHandler(pwd, confirmPwd);
        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const componentStyles: MessageFilterStyles = styles.messageFilter || {};
const currentDateTime = new Date().toLocaleString();

    const confirmHandler = () => {
        if (email) {
            checkUserExists({
                email,
                currentDateTime,
                onComplete(result) {
                    handleExistingUser(result);
                },
            });
        }
    };

    const handleExistingUser = (data: any) => {
        setIsLoading(false);
        setIsFormReady(true);
        setIsExistingUser(false);
        if (data.apiResult?.isDataFound) {
            setIsExistingUser(true);
            setIsFormReady(false);
        }
    };

    const loginHandler = () => {
        if (email && password) {
            setIsSignInClicked(true);
            signIn({
                userName: email,
                password,
                rememberMe: false,
                returnUrl: onePageCheckoutPageUrl,
                onError: (error, statusCode) => {
                    if (statusCode === 422) {
                        setIsChangePasswordModalOpen(true);
                        return;
                    }
                    setIsSignInClicked(false);
                    setErrorMessage(error);
                },
            });
        }
    };

    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };
    const [openTabs, setOpenTabs] = useState<number[]>(tabState.openTabs);
    const [completedTabs, setCompletedTabs] = useState<number[]>(tabState.completedTabs);
    const [currentTab, setCurrentTab] = useState<number>(tabState.currentTab);

    const completeAction = (index: number) => {
        setCurrentTab(index + 1);
        if (!completedTabs.includes(index)) {
            setCompletedTabs([...completedTabs, index]);
            setOpenTabs(prev => [...prev, index + 1]);
        }
    };

    React.useEffect(() => {
        SetOnePageTabData({
            openTabs,
            completedTabs,
            currentTab,
        });
    }, [openTabs, completedTabs, currentTab, SetOnePageTabData]);

    const cusPasswordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    useEffect(() => {
        if (isAuthenticated) {
            setOnePageCreateAccountData({ firstName: userFirstName, lastName: userLastName, email: userName });
            completeAction(0);
        }
    }, [isAuthenticated, userFirstName, userLastName, userName]);

    if (isAuthenticated && !notYouClicked) {
        return (
            <StyledWrapper>
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.companyDetailsGridItem}>
                        <StyledWrapper {...styles.userDetailsWrapper}>
                            <Typography {...styles.userLabel}>
                                {userLabel}{" "}
                                <Typography
                                    onClick={() => {
                                        signOut();
                                    }}
                                    {...styles.changeLabel}
                                >
                                    (Not You?)
                                </Typography>
                            </Typography>
                            <Typography {...styles.userName}>{userName}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.logOutGridItem}>
                        <Clickable
                            onClick={() => {
                                signOut();
                            }}
                            {...styles.logOut}
                        >
                            {translate("Log Out")}
                        </Clickable>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper>
            <LoadingOverlay loading={isSubmitted} spinnerProps={{ color: "primary" }}>
                <GridContainer {...styles.mainGridContainer}>
                    <GridContainer className="personalInfoDiv">
                        <GridItem {...styles.fieldsGridItem}>
                            <TextField
                                {...styles.firstNameTextField}
                                name="firstName"
                                value={firstName}
                                data-test-selector="onePageCheckout_firstName"
                                onChange={e => firstNameChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.firstName && formErrors.firstName}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.firstName ? AlertTriangle : undefined,
                                }}
                                placeholder="Enter first name *"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.fieldsGridItem}>
                            <TextField
                                {...styles.lastNameTextField}
                                name="lastName"
                                value={lastName}
                                data-test-selector="onePageCheckout_lastName"
                                onChange={e => lastNameChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.lastName && formErrors.lastName}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.lastName ? AlertTriangle : undefined,
                                }}
                                placeholder="Enter last name *"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.fieldsGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="email"
                                name="email"
                                onChange={e => emailChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={email}
                                data-test-selector="onePageCheckout_email"
                                placeholder="Enter E-mail Address *"
                                required
                                error={!!formErrors.email && formErrors.email}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.email ? AlertTriangle : undefined,
                                }}
                            />
                        </GridItem>
                        {isLoading && (
                            <GridItem width={12}>
                                <StyledWrapper {...styles.centeringWrapper}>
                                    <LoadingSpinner {...styles.spinner} />
                                </StyledWrapper>
                            </GridItem>
                        )}

                        {isExistingUser && (
                            <>
                                <GridItem width={12}>
                                    <Typography>
                                        {translate("Welcome back!")}{" "}
                                        <Typography style={{ color: "red" }}>
                                            {translate("An account already exists for this email address. Please enter your password below to log in.")}
                                        </Typography>
                                    </Typography>
                                </GridItem>
                                <GridItem width={12}>
                                    <TextField
                                        placeholder="Enter your password"
                                        type="password"
                                        name="password"
                                        value={password}
                                        onChange={e => passwordChangeHandler(e.currentTarget)}
                                        onBlur={e => pwdHandler(e.currentTarget)}
                                        error={
                                            cusPasswordErrorMessage || (!!formErrors.password && formErrors.password)
                                        }
                                    />
                                </GridItem>

                                <GridItem {...styles.lostYourPasswordSection}>
                                    <Clickable   onClick={() => setIsResetPasswordModalOpen(true)} {...styles.lostYourPassword}>{translate("Lost your password.")}</Clickable>
                                    <Modal
                                        headline={translate("Reset Password")}
                                        {...styles.resetPasswordModal}
                                        isOpen={isResetPasswordModalOpen}
                                        handleClose={() => onResetPasswordClose()}
                                        data-test-selector="resetPasswordModal"
                                    >
                                        <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                                    </Modal>
                                </GridItem>
                                
                                <GridItem {...styles.loginButtonSection}>
                                    <Button
                                        {...styles.loginBtn}
                                        key="addAll"
                                        onClick={loginHandler}
                                        disabled={isSignInClicked}
                                        data-test-selector="cartActionsAddAllToList"
                                    >
                                        {translate("Log In")}
                                    </Button>
                                </GridItem>
                            </>
                        )}

                        {/* {!isExistingUser && (
                            <GridItem {...styles.loginButtonSection} width={12}>
                                <Button
                                    {...styles.loginBtn}
                                    key="addAll"
                                    onClick={confirmHandler}
                                    data-test-selector="cartActionsAddAllToList"
                                >
                                    {translate("Confirm")}
                                </Button>
                            </GridItem>
                        )} */}
                    </GridContainer>
                </GridContainer>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OnePageLoginFormNew);
